
	import {Component, Vue, Watch} from 'vue-property-decorator';
	import IResource from "@/IResource";
	import FloatingBottomBar from '@/components/reservation/FloatingBottomBar.vue';
	import { mixins } from 'vue-class-component';
	import { Device } from '@/mixins/Device';

	interface ITimeOption {
		value: number;
		label: string;
		enabled: boolean;
	}

	@Component({
		components: {
			FloatingBottomBar
		}
	})
	export default class StepTime extends mixins(Device) {

		public fromtime: number = this.$store.state.fromtime;
		public totime: number = this.$store.state.totime;

		public startOptions: {[key: number]: ITimeOption} = {};
		public endOptions: {[key: number]: ITimeOption} = {};

		public validating: boolean = false;

		get fromtimeError(): boolean {
			return this.validating && this.fromtime === 0;
		}

		get totimeError(): boolean {
			return this.validating && (this.totime === 0 || !(this.totime in this.filteredEndOptions));
		}

		get availability(): { [key: string]: number[][] } {
			return this.$store.state.availability;
		}

		get resource(): IResource {
			return this.$store.state.resource;
		}

		get slotSize(): number {
			return this.resource.slot_size;
		}

		get mintime(): Date {
			return this.$store.state.minreservationtime;
		}

		get maxDuration(): number {
			if (!this.resource.max_duration) {
				return 0;
			}
			if (this.resource.type !== 'singleday') {
				return 0;
			}
			// reduce max duration to next possible slot size
			let max: number = Math.floor(this.$store.state.resource.max_duration / this.slotSize) * this.slotSize;
			// increase max to slot size
			if (max < this.slotSize) {
				max = this.slotSize;
			}
			return max;
		}

		get dateStart(): Date {
			return this.$store.state.startdate;
		}

		get dateEnd(): Date {
			if (this.$store.state.enddate === null) {
				return this.$store.state.startdate;
			}
			return this.$store.state.enddate;
		}

		get isSameDay(): boolean {
			return this.dateKeyEnd === this.dateKeyStart;
		}

		get dateKeyStart(): string {
			let d = this.dateStart;
			return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
		}

		get dateKeyEnd(): string {
			let d = this.dateEnd;
			return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
		}

		get filteredEndOptions(): {[key: number]: ITimeOption} {
			let options: {[key: number]: ITimeOption} = {};
			let disabledOptionFound = false;
			Object.keys(this.endOptions).forEach(keyStr => {
				const key: number = parseInt(keyStr, 10);
				//if (this.dateKeyStart !== this.dateKeyEnd || this.endOptions[key].value > this.fromtime) {
				if (
					!this.isSameDay
					|| this.endOptions[key].value > this.fromtime && (this.maxDuration === 0 || this.endOptions[key].value <= this.fromtime + this.maxDuration)
				) {
					if (!disabledOptionFound) {
						options[key] = JSON.parse(JSON.stringify(this.endOptions[key]));
						if (!options[key].enabled) {
							options[key].enabled = true; // make starttime of next reservation selectable as endtime
						}
					}
					if (!this.endOptions[key].enabled) {
						disabledOptionFound = true;
					}
				}
			});

			return options;
		}

		get hasSelectableTimes(): boolean {
			if (Object.keys(this.startOptions).length > 0) {
				for (let key in this.startOptions) {
					if (this.startOptions[key].enabled) {
						return true;
					}
				}
			}
			return false;
		}

		public mounted() {
			this.buildTimeOptions();
			this.$gtag.pageview( {page_path: '/step-time' });
		}

		@Watch('availability')
		public watchAvailability() {
			this.buildTimeOptions();
		}

		@Watch('fromtime')
		public watchFromtime() {
			if (this.isSameDay) {
				// automatically select a feasible end time
				let endOptionKeys = Object.keys(this.filteredEndOptions);
				if (endOptionKeys.length > 0) {
					if (this.maxDuration && this.totime > this.fromtime + this.maxDuration) {
						this.totime = this.filteredEndOptions[parseInt(endOptionKeys[endOptionKeys.length - 1])].value;
					}
					if (this.totime > 0 && this.totime <= this.fromtime) {
						this.totime = 0;
					}
				}
			}
		}

		public buildTimeOptions() {
			let minminutes: number = 0;
			if (this.dateStart < this.mintime) {
				minminutes = this.mintime.getHours() * 60 + this.mintime.getMinutes();
			}
			let start = this.resource.starttime * 60;
			let end = this.resource.endtime * 60;
			let step = this.resource.slot_size;

			let minutes;
			for (minutes = start; minutes <= end; minutes += step) {
				let mins: number|string = minutes % 60;
				if (mins < 10) {
					mins = "0" + mins;
				}
				let hours: number|string = Math.floor(minutes / 60);
				if (hours < 10) {
					hours = "0" + hours;
				}
				if (minutes > minminutes) {
					if (minutes < end) {
						let dataStart: ITimeOption = {
							value: minutes,
							label: hours + ':' + mins,
							enabled: true
						};
						this.$set(this.startOptions, minutes, dataStart);
					}
				}
				let dataEnd: ITimeOption = {
					value: minutes,
					label: hours + ':' + mins,
					enabled: true
				};
				if (this.dateStart.toDateString() !== this.dateEnd.toDateString()) {
					this.$set(this.endOptions, minutes, dataEnd);
				} else if (minutes > minminutes && minutes > start) {
					this.$set(this.endOptions, minutes, dataEnd);
				}
			}

			if (this.availability && this.availability[this.dateKeyStart]) {
				this.availability[this.dateKeyStart].forEach((reservation) => {
					let minutes;
					for (minutes = reservation[0]; minutes < reservation[1]; minutes++) {
						if (this.startOptions[minutes]) {
							this.startOptions[minutes].enabled = false;
						}
					}
				})
			}
			if (this.availability && this.availability[this.dateKeyEnd]) {
				this.availability[this.dateKeyEnd].forEach((reservation) => {
					let minutes;
					for (minutes = reservation[0]; minutes < reservation[1]; minutes++) {
						if (this.endOptions[minutes]) {
							this.endOptions[minutes].enabled = false;
						}
					}
				})
			}
		}

		public formattedDuration(start: number, end: number): string {
			if (!this.isSameDay || this.fromtime === 0) {
				return '';
			}
			let duration: string = '';
			let diff = end - start;
			if (start < end) {
				if (diff >= 60) {
					if (Math.floor(diff / 60) === 1) {
						duration += Math.floor(diff / 60) + ' Stunde';
					} else {
						duration += Math.floor(diff / 60) + ' Stunden';
					}
					if (Math.round(diff % 60) > 0) {
						duration += ' ' + (Math.round(diff % 60) + '').padStart(2, '0') + 'min';
					}
				} else {
					duration = diff + 'min'
				}
				return ' (' + duration + ')';
			}
			return '';
		}

		public goToNextPage() {
			if (this.fromtime === 0 || this.totime === 0 || !(this.totime in this.filteredEndOptions)) {
				this.validating = true;
			} else {
				this.validating = false;
				this.$store.commit('SET_FROMTIME', this.fromtime);
				this.$store.commit('SET_TOTIME', this.totime);
				this.$store.commit('SET_STEP', 'step3')
			}
		}
		public goToLastPage() {
			this.$store.commit('SET_STEP', 'step1')
		}
	}
